import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPaddle, onPaddleEvent } from '../services/paddle';
import { AuthShell } from '../components/auth/AuthField';
import { Doodle } from '../components/paper';

type Phase = 'opening' | 'done' | 'closed' | 'missing' | 'failed';

/**
 * Paddle's default payment link (features/paddle-billing/notes/account-setup.md).
 *
 * Paddle sends people here with `?_ptxn=txn_…` — from invoices, and from the
 * "update your payment method" emails a failed renewal triggers, which is how a
 * `past_due` subscriber gets back in. Paddle.js opens that checkout by itself once
 * initialized. Public on purpose: the person following an email link may not be
 * signed in on this browser.
 */
export const Checkout: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>(() =>
    new URLSearchParams(window.location.search).has('_ptxn') ? 'opening' : 'missing',
  );

  useEffect(() => {
    if (phase !== 'opening') return undefined;
    const off = onPaddleEvent((event) => {
      if (event.name === 'checkout.completed') setPhase('done');
      else if (event.name === 'checkout.closed') setPhase((p) => (p === 'done' ? p : 'closed'));
    });
    getPaddle().catch(() => setPhase('failed'));
    return off;
    // Initialize once, on arrival; the phase changes after that are ours.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === 'done' && user) navigate('/subscription/success', { replace: true });
  }, [phase, user, navigate]);

  const copy: Record<Phase, { title: string; body: string }> = {
    opening: { title: 'Opening checkout…', body: 'This only takes a moment.' },
    done: { title: "You're all set.", body: 'Your payment went through. You can close this tab.' },
    closed: { title: 'Checkout closed.', body: 'Nothing was charged.' },
    missing: { title: 'Nothing to pay here.', body: 'This link is missing its checkout.' },
    failed: { title: "Checkout didn't load.", body: 'Check your connection and refresh this page.' },
  };

  return (
    <AuthShell>
      <div className="flex flex-col items-center pt-12 text-center">
        <Doodle kind="page" />
        <h1 className="pb-heading-2 m-0 mt-6">{copy[phase].title}</h1>
        <p className="m-0 mt-2 max-w-[20rem] font-body text-[0.9375rem] text-on-surface-variant">
          {copy[phase].body}
        </p>
        {phase !== 'opening' && (
          <Link
            to={user ? '/account' : '/login'}
            className="mt-6 font-body text-[0.875rem] font-semibold text-accent no-underline hover:underline"
          >
            {user ? 'Back to your account' : 'Sign in to PaperBrain'}
          </Link>
        )}
      </div>
    </AuthShell>
  );
};
